import { EditOutlined, ExclamationCircleFilled, ShopOutlined, WarningFilled } from "@ant-design/icons";
import { Col, Row, Typography } from "antd";
import PickupWarehouseSelect from "features/Order/Detail/components/PickupWarehouseSelect";
import FormInput from "components/FormInput";
import { INPUT_TYPE } from "config/constants";
import { CARRIER_METHOD, CARRIER_METHOD_LABEL, MAXIMUM_LENGTH_CURRENCY, SHIPPING_TYPE } from "features/Cart/constants";
import { useFlags } from "flagsmith/react";
import { t } from "i18next";
import { compact, isEmpty, isNil, join } from "lodash";
import { useEffect, useState } from "react";
import { formatCurrency, validator } from "utils/helperFuncs";
import useAdditionalDeliver from "./useAdditionalDeliver";

const AdditionalDeliver = ({
  dataOrder,
  shippingType,
  handleUpdate,
  refetchCart,
  useInOrder,
  dataShippingProvider,
  form,
  loadingShippingProvider,
  isShippingTypeInvalid,
}) => {
  const { shippingProviderOptions, instantProviderOptions, fetchShippingProviderOptions } = useAdditionalDeliver();

  const validFee = (_, value) => {
    if (isNil(value)) {
      return Promise.reject(new Error(t("cart.blankFee")));
    }
    if (value.toString().length > MAXIMUM_LENGTH_CURRENCY) {
      return Promise.reject(new Error(t("cart.maximumFee")));
    }
    return Promise.resolve();
  };

  const getShippingMethod = (name, money, imgUrl = null) => {
    return (
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Row style={{ alignItems: "center", gap: "5px" }}>
          {imgUrl && <img src={imgUrl} alt="" height={20} />}
          {name}
        </Row>
        <Typography.Link strong>{money}</Typography.Link>
      </div>
    );
  };

  const [textErrorNotSupport, setTextErrorNotSupport] = useState("");
  const [pickupModalVisible, setPickupModalVisible] = useState(false);
  const [selectedPickup, setSelectedPickup] = useState({
    warehouseID: undefined,
    receiverID: undefined,
    warehouse: undefined,
    receiver: undefined,
  });

  useEffect(() => {
    const info = dataOrder?.shippingInfo?.pickupInfo;
    if (info) {
      setSelectedPickup({
        warehouseID: info.warehouseID,
        receiverID: info.receiver?.id,
        warehouse: info.warehouse,
        receiver: info.receiver,
      });
    } else {
      // Reset selectedPickup when there's no pickupInfo
      setSelectedPickup({
        warehouseID: undefined,
        receiverID: undefined,
        warehouse: undefined,
        receiver: undefined,
      });
    }
  }, [dataOrder]);

  // Cart vào màn đã có sẵn PTVC + providerID (VD sửa đơn) -> Select cần biết TÊN provider ngay để
  // hiển thị, không thể chờ tới lúc user mở dropdown (lúc đó Select chỉ có `value` là id, không có
  // label tương ứng nên hiện luôn id thô).
  useEffect(() => {
    if (shippingType === SHIPPING_TYPE.DELIVER_3PARTY || shippingType === SHIPPING_TYPE.DELIVER_3PARTY_INSTANT) {
      fetchShippingProviderOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingType, dataOrder?.id]);

  // Cart lưu chung 1 `providerID` cho cả "Chuyển phát" và "Hoả tốc" nhưng 2 dropdown có tập ĐVVC rời
  // nhau. Đổi PTVC mà ĐVVC cũ không thuộc nhóm mới thì Select không khớp option nào và hiện id thô —
  // xoá để buộc chọn lại, tránh gửi lên ĐVVC sai carrierMethod.
  useEffect(() => {
    const options =
      shippingType === SHIPPING_TYPE.DELIVER_3PARTY_INSTANT ? instantProviderOptions : shippingProviderOptions;
    if (!options?.length) return;

    const providerIDPath = ["shippingConfiguration", "deliver3PartyConfig", "providerID"];
    const currentProviderID = form?.getFieldValue(providerIDPath);
    if (currentProviderID && !options.some((option) => option?.value === currentProviderID)) {
      form?.setFields([{ name: providerIDPath, value: undefined }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingType, shippingProviderOptions, instantProviderOptions]);

  useEffect(() => {
    const carrierMethodShipping = dataOrder?.shippingConfiguration?.deliver3PartyConfig?.carrierMethod;
    const providerIDShipping = dataOrder?.shippingConfiguration?.deliver3PartyConfig?.providerID;

    const getShippingMethods = dataShippingProvider?.find(
      (item) => item?.method === carrierMethodShipping && item?.carrierMinFee !== null && item?.carrierMaxFee !== null
    );
    const getShippingProvider = getShippingMethods?.providers?.find(
      (item) => item.id === `${providerIDShipping} - ${carrierMethodShipping}`
    );
    const checkShipping = () => {
      if (carrierMethodShipping !== null && providerIDShipping !== null) {
        return getShippingProvider;
      } else if (carrierMethodShipping !== null && providerIDShipping === null) {
        return getShippingMethods;
      } else {
        return true;
      }
    };
    if (dataOrder && !loadingShippingProvider) {
      if (
        dataOrder?.items?.length > 0 &&
        (!dataShippingProvider || (dataShippingProvider && (!checkShipping() || checkShipping() === null)))
      ) {
        setTextErrorNotSupport(t("cart.textErrorNotSupport"));
      } else {
        setTextErrorNotSupport("");
      }
    }
  }, [dataShippingProvider, dataOrder, loadingShippingProvider]);

  const convertDataShippingProvider = dataShippingProvider?.map((item) => ({
    name: item.method,
    id: item.id,
    priceRange:
      item.carrierMinFee != null || item.carrierMaxFee != null
        ? item.carrierMinFee === item.carrierMaxFee
          ? formatCurrency(item.carrierMaxFee)
          : `${formatCurrency(item.carrierMinFee ?? 0)} - ${formatCurrency(item.carrierMaxFee ?? 0)}`
        : formatCurrency(0),
    minPrice: item.carrierMinFee,
    maxPrice: item.carrierMaxFee,
    isDisabled: (item.carrierMinFee == null || item.carrierMaxFee == null) && true,
    children: item?.providers?.map((i) => ({
      id: i.id,
      name: i.name,
      priceRange: formatCurrency(i.fee),
      fee: i.fee,
      imgUrl: i.logo_url,
    })),
  }));

  const treeData = convertDataShippingProvider?.map((method) => ({
    value: method?.name,
    disabled: method?.isDisabled,
    minPrice: method?.minPrice,
    maxPrice: method?.maxPrice,
    title: getShippingMethod(CARRIER_METHOD_LABEL[method?.name], method?.priceRange),
    children: method?.children?.map((childMethod) => ({
      value: childMethod?.id,
      fee: childMethod.fee,
      title: getShippingMethod(childMethod?.name, childMethod?.priceRange, childMethod?.imgUrl),
    })),
  }));

  // `enable_new_shipping_provider` phải khớp với flag `useList.js` dùng để fetch `dataShippingProvider`
  // — nếu lệch nhau, TreeSelect ở đây render rỗng do data không được fetch dù UI vẫn cố hiển thị
  // dạng TreeSelect (hoặc ngược lại).
  const flags = useFlags(["enable_new_shipping_provider"]);
  const idEnableNewShippingProvider = flags.enable_new_shipping_provider.enabled;

  const delivers = {
    [`${SHIPPING_TYPE.BUS}`]: idEnableNewShippingProvider ? (
      <>
        <Col span={8}>
          <FormInput
            formItemOptions={{
              name: ["shippingConfiguration", "busConfig", "busName"],
              rules: [{ required: true, message: t("cart.blankBusName") }],
            }}
            inputOptions={{
              placeholder: t("cart.busNamePlaceholder"),
              onBlur: () => handleUpdate(),
              maxLength: 255,
            }}
          />
        </Col>

        <Col span={8}>
          <FormInput
            formItemOptions={{
              rules: [
                validator({
                  type: "phone",
                }),
              ],
              name: ["shippingConfiguration", "busConfig", "telephone"],
            }}
            inputOptions={{
              placeholder: t("cart.telephonePlaceholder"),
              onBlur: () => handleUpdate(),
            }}
          />
        </Col>

        <Col span={8}>
          <FormInput
            formItemOptions={{ name: ["shippingConfiguration", "busConfig", "busStation"] }}
            inputOptions={{
              placeholder: t("cart.busStationPlaceholder"),
              onBlur: () => handleUpdate(),
              maxLength: 255,
            }}
          />
        </Col>
      </>
    ) : (
      <>
        <Col span={12}>
          <FormInput
            formItemOptions={{
              name: ["shippingConfiguration", "busConfig", "busName"],
              rules: [{ required: true, message: t("cart.blankBusName") }],
            }}
            inputOptions={{
              placeholder: t("cart.busNamePlaceholder"),
              onBlur: () => handleUpdate(),
            }}
          />
        </Col>

        <Col span={12}>
          <FormInput
            formItemOptions={{ name: ["shippingConfiguration", "busConfig", "location"] }}
            inputOptions={{
              placeholder: t("cart.locationPlaceholder"),
              onBlur: () => handleUpdate(),
            }}
          />
        </Col>

        <Col span={12}>
          <FormInput
            formItemOptions={{ name: ["shippingConfiguration", "busConfig", "telephone"] }}
            inputOptions={{
              placeholder: t("cart.telephonePlaceholder"),
              onBlur: () => handleUpdate(),
            }}
          />
        </Col>

        <Col span={12}>
          <FormInput
            formItemOptions={{ name: ["shippingConfiguration", "busConfig", "busStation"] }}
            inputOptions={{
              placeholder: t("cart.busStationPlaceholder"),
              onBlur: () => handleUpdate(),
            }}
          />
        </Col>
      </>
    ),
    [`${SHIPPING_TYPE.DELIVER_3PARTY}`]: idEnableNewShippingProvider ? (
      <Col span={24}>
        <FormInput
          type={INPUT_TYPE.TREE_SELECT}
          formItemOptions={{
            style: { width: "100%", marginBottom: "8px" },
            name: ["shippingConfiguration", "deliver3PartyConfig", "providerID"],
            rules: [{ required: true, message: t("cart.blankServiceCode") }],
          }}
          inputOptions={{
            placeholder: t("cart.serviceCodePlaceholder"),
            treeData: treeData ?? [],
            showSearch: true,
            treeNodeFilterProp: "title",
            filterTreeNode: (search, item) => {
              if (item?.title?.props?.children) {
                const titleHtml = item?.title?.props?.children[0]?.props?.children[1];
                return titleHtml?.toLowerCase()?.includes(search?.toLowerCase());
              }
            },
            onChange: (value) => {
              function findOptionById(tree, id) {
                if (!tree) return null;
                for (const option of tree) {
                  if (option.value === id) return option;
                  if (option.children) {
                    const result = findOptionById(option.children, id);
                    if (result) return result;
                  }
                }
                return null;
              }
              const selectedOption = findOptionById(treeData, value);
              const parentMethod = treeData?.find((method) => method?.children?.includes(selectedOption));
              handleUpdate(
                undefined,
                selectedOption?.minPrice,
                selectedOption?.maxPrice,
                selectedOption?.fee,
                parentMethod?.value
              );
            },
          }}
        />
        <span style={{ display: "flex", alignItems: "center", gap: "6px", margin: "-5px 0px 10px 0px" }}>
          <Typography.Text style={{ color: "#ff4d4f" }}>{textErrorNotSupport}</Typography.Text>
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "0px" }}>
          <ExclamationCircleFilled style={{ color: "#EF9351" }} />
          <Typography.Text style={{ color: "#EF9351" }}>{t("cart.infowarningchooseproduct")}</Typography.Text>
        </span>
      </Col>
    ) : (
      <Col span={24}>
        <FormInput
          type={INPUT_TYPE.SELECT}
          formItemOptions={{
            style: { width: "100%" },
            name: ["shippingConfiguration", "deliver3PartyConfig", "providerID"],
            rules: [{ required: true, message: "Vui lòng chọn đơn vị vận chuyển" }],
          }}
          inputOptions={{
            placeholder: t("cart.serviceCodePlaceholder"),
            options: shippingProviderOptions,
            // Chỉ gọi API lấy danh sách ĐVVC khi user thực sự mở dropdown, không fetch ngay lúc
            // chọn PTVC.
            onDropdownVisibleChange: (open) => open && fetchShippingProviderOptions(),
            onChange: () => handleUpdate(),
          }}
        />
      </Col>
    ),
    [`${SHIPPING_TYPE.DELIVER_3PARTY_INSTANT}`]: (
      <Col span={24}>
        <FormInput
          type={INPUT_TYPE.SELECT}
          formItemOptions={{
            style: { width: "100%", marginBottom: 2 },
            name: ["shippingConfiguration", "deliver3PartyConfig", "providerID"],
            rules: [{ required: true, message: t("cart.blankServiceCode") }],
          }}
          inputOptions={{
            placeholder: t("cart.serviceCodePlaceholder"),
            options: instantProviderOptions,
            showSearch: true,
            optionFilterProp: "label",
            // Làm mới danh sách ĐVVC khi user chủ động mở lại dropdown.
            onDropdownVisibleChange: (open) => open && fetchShippingProviderOptions(),
            onChange: (value) => {
              const selectedOption = instantProviderOptions?.find((option) => option?.value === value);
              handleUpdate(undefined, undefined, undefined, selectedOption?.fee, CARRIER_METHOD.INSTANT);
            },
          }}
        />
      </Col>
    ),
    [`${SHIPPING_TYPE.PICK_UP_COUNTER}`]: (
      <>
        <Col span={24}>
          <div
            onClick={() => setPickupModalVisible(true)}
            style={{
              border: "1px solid #E5E7EB",
              borderRadius: 12,
              overflow: "hidden",
              cursor: "pointer",
              background: "#fff",
            }}
          >
            <Row align="middle" wrap={false} style={{ padding: "12px 16px" }}>
              <Col style={{ width: 130, flexShrink: 0 }}>
                <Row align="middle" gutter={6} wrap={false}>
                  <Col>
                    <ShopOutlined style={{ color: "#6B7280", fontSize: 13 }} />
                  </Col>
                  <Col>
                    <Typography.Text style={{ fontWeight: 600, fontSize: 13, color: "#374151" }}>
                      Nhận tại kho
                    </Typography.Text>
                  </Col>
                </Row>
              </Col>
              <Col flex="auto">
                {selectedPickup?.receiver ? (
                  <Typography.Text strong style={{ fontSize: 13, color: "#374151" }}>
                    {join(compact([selectedPickup.receiver.fullName, selectedPickup.receiver.phone]), " | ")}
                  </Typography.Text>
                ) : (
                  <Typography.Text style={{ fontSize: 13, color: "#9CA3AF" }}>Chưa có người nhận</Typography.Text>
                )}
              </Col>
              <Col>
                <EditOutlined style={{ color: "#2563EB", fontSize: 13 }} />
              </Col>
            </Row>
          </div>
          {!selectedPickup?.receiver && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
              <WarningFilled style={{ color: "#ff4d4f", fontSize: 13 }} />
              <Typography.Text style={{ color: "#ff4d4f", fontSize: 13 }}>
                {t("cart.missingPickupInfo")}
              </Typography.Text>
            </div>
          )}
        </Col>

        <PickupWarehouseSelect
          visible={pickupModalVisible}
          onClose={() => setPickupModalVisible(false)}
          sellerID={dataOrder?.sellerID}
          initialWarehouseID={selectedPickup?.warehouseID}
          initialReceiverID={selectedPickup?.receiverID}
          onConfirm={({ warehouseID, receiverID, warehouse, receiver }) => {
            setSelectedPickup({ warehouseID, receiverID, warehouse, receiver });
            handleUpdate({ warehouseID, pickupReceiverID: receiverID }).then(() => refetchCart());
            setPickupModalVisible(false);
          }}
        />
      </>
    ),
    [`${SHIPPING_TYPE.URBAN_COD}`]:
      isEmpty(useInOrder) || useInOrder?.changeShippingFee ? (
        <Col span={24}>
          <FormInput
            type={INPUT_TYPE.CURRENCY_INPUT}
            formItemOptions={{
              style: { width: "100%", marginBottom: 2 },
              name: ["shippingConfiguration", "urbanConfig", "fee"],
              rules: [
                {
                  validator: validFee,
                },
              ],
            }}
            inputOptions={{
              placeholder: t("cart.urbanCODFeePlaceholder"),
              // `InputNumber` của AntD có width mặc định cố định, `Col span` không kéo giãn được.
              style: { width: "100%" },
              onBlur: () => handleUpdate().then(() => refetchCart()),
            }}
          />
        </Col>
      ) : (
        <>
          <Col span={6}>
            <Typography.Text strong>{t("order.orderDetail.shippingFee")}:</Typography.Text>
          </Col>
          <Col span={6}>
            <Typography.Text strong style={{ color: "#052987" }}>
              {formatCurrency(10000)}
            </Typography.Text>
          </Col>
        </>
      ),
  };

  // Đơn rớt xuống dưới ngưỡng, PTVC cũ không còn hợp lệ — không hiện khối phụ cũ, chờ chọn lại.
  if (isShippingTypeInvalid) return null;

  const content = shippingType ? delivers[`${shippingType}`] : delivers.BUS;

  // Thụt lề trái 16% để thẳng hàng với cột nhập của "Phương thức giao hàng"/"Người gửi, Người nhận".
  return (
    <Col span={24}>
      <div style={{ paddingLeft: "175px" }}>
        <Row gutter={16}>{content}</Row>
      </div>
    </Col>
  );
};

export default AdditionalDeliver;
